/**
 * Interactive Site Plan Component
 * Displays an SVG-based map with clickable unit polygons
 */
import { useState, useEffect, useCallback } from 'react';
import { getFilteredSiteMapData, type SiteMapPolygon } from '@/lib/site-map-data';
import type { PropertyUnit } from '@/lib/units';
import type { UnitFilters } from '@/types/map';
import { getSiteMapConfig, type SiteMapConfig } from '@/lib/site-config';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import type { InteractiveMapProps } from '@/types/map';
import { formatFloorDisplay, getStatusColorHex } from '@/lib/utils';
import { trackUnitClick } from '@/lib/unit-interest';
import { getFilterLabel } from '@/lib/filter-validation';
import { Filter, X, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { CompassRose } from './CompassRose';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';

interface InteractiveSitePlanProps extends InteractiveMapProps {
  onViewFilterChange?: (filter: string) => void;
  onAvailabilityFilterChange?: (filter: string) => void;
  onCategoryFilterChange?: (filter: string) => void;
  onFloorFilterChange?: (filter: string) => void;
}

export function InteractiveSitePlan({ 
  onUnitClick, 
  selectedUnit,
  viewFilter = 'all',
  availabilityFilter = 'all',
  categoryFilter = 'all',
  floorFilter = 'all',
  onViewFilterChange,
  onAvailabilityFilterChange,
  onCategoryFilterChange,
  onFloorFilterChange,
}: InteractiveSitePlanProps) {
  const [units, setUnits] = useState<PropertyUnit[]>([]);
  const [siteMapPolygons, setSiteMapPolygons] = useState<SiteMapPolygon[]>([]);
  const [polygonUnitsMap, setPolygonUnitsMap] = useState<Map<string, PropertyUnit[]>>(new Map());
  const [hoveredUnit, setHoveredUnit] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [internalViewFilter, setInternalViewFilter] = useState(viewFilter);
  const [internalAvailabilityFilter, setInternalAvailabilityFilter] = useState(availabilityFilter);
  const [internalCategoryFilter, setInternalCategoryFilter] = useState(categoryFilter);
  const [internalFloorFilter, setInternalFloorFilter] = useState(floorFilter);
  const [unitsAtLocation, setUnitsAtLocation] = useState<PropertyUnit[] | null>(null);
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [siteMapConfig, setSiteMapConfig] = useState<SiteMapConfig | null>(null);

  // Default viewBox dimensions
  const DEFAULT_VIEWBOX_WIDTH = 1728;
  const DEFAULT_VIEWBOX_HEIGHT = 864;

  // Load site map config for center offset
  useEffect(() => {
    getSiteMapConfig().then(setSiteMapConfig);
  }, []);

  // Compute viewBox with center offset applied
  const computedViewBox = siteMapConfig?.centerOffset !== undefined
    ? `${siteMapConfig.centerOffset} 0 ${DEFAULT_VIEWBOX_WIDTH - Math.abs(siteMapConfig.centerOffset)} ${DEFAULT_VIEWBOX_HEIGHT}`
    : `0 0 ${DEFAULT_VIEWBOX_WIDTH} ${DEFAULT_VIEWBOX_HEIGHT}`;

  // Use controlled values if callbacks provided, otherwise use internal state
  const activeViewFilter = onViewFilterChange ? viewFilter : internalViewFilter;
  const activeAvailabilityFilter = onAvailabilityFilterChange ? availabilityFilter : internalAvailabilityFilter;
  const activeCategoryFilter = onCategoryFilterChange ? categoryFilter : internalCategoryFilter;
  const activeFloorFilter = onFloorFilterChange ? floorFilter : internalFloorFilter;

  // Filter change handlers
  const handleViewFilterChange = (filter: string) => {
    if (onViewFilterChange) {
      onViewFilterChange(filter);
    } else {
      setInternalViewFilter(filter as typeof viewFilter);
    }
  };

  const handleAvailabilityFilterChange = (filter: string) => {
    if (onAvailabilityFilterChange) {
      onAvailabilityFilterChange(filter);
    } else {
      setInternalAvailabilityFilter(filter as typeof availabilityFilter);
    }
  };

  const handleCategoryFilterChange = (filter: string) => {
    if (onCategoryFilterChange) {
      onCategoryFilterChange(filter);
    } else {
      setInternalCategoryFilter(filter as typeof categoryFilter);
    }
  };

  const handleFloorFilterChange = (filter: string) => {
    if (onFloorFilterChange) {
      onFloorFilterChange(filter);
    } else {
      setInternalFloorFilter(filter as typeof floorFilter);
    }
  };

  const loadUnits = useCallback(async () => {
    setIsLoading(true);
    try {
      // Build filters object
      const filters: UnitFilters = {};
      
      if (activeViewFilter !== 'all') {
        filters.viewType = activeViewFilter;
      }
      if (activeAvailabilityFilter !== 'all') {
        filters.availability = activeAvailabilityFilter;
      }
      if (activeCategoryFilter !== 'all') {
        filters.category = activeCategoryFilter;
      }
      if (activeFloorFilter !== 'all') {
        filters.floor = activeFloorFilter;
      }

      // Single optimized query with caching and filtering
      const { data, error } = await getFilteredSiteMapData(filters);
      
      if (error) {
        console.error('Error loading site map data:', error);
      } else if (data) {
        setUnits(data.units);
        setSiteMapPolygons(data.polygons);
        setPolygonUnitsMap(data.polygonUnitsMap);
        
        // Debug logging
        const blockACount = data.units.filter(u => u.block === 'A').length;
        const blockBCount = data.units.filter(u => u.block === 'B').length;
        console.log(`📊 Loaded ${data.units.length} units: Block A: ${blockACount}, Block B: ${blockBCount}`);
        console.log(`📊 Loaded ${data.polygons.length} polygons, ${data.polygonUnitsMap.size} with units`);
      }
    } catch (error) {
      console.error('Error loading units:', error);
    } finally {
      setIsLoading(false);
    }
  }, [activeViewFilter, activeAvailabilityFilter, activeCategoryFilter, activeFloorFilter]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  function handleUnitClick(unitNumber: string) {
    onUnitClick(unitNumber);
  }

  // Helper function to check if two polygons have the same/similar coordinates
  function arePolygonsSame(coords1: { type?: string; points?: number[][] } | null, coords2: { type?: string; points?: number[][] } | null): boolean {
    if (!coords1 || !coords2 || coords1.type !== 'Polygon' || coords2.type !== 'Polygon') {
      return false;
    }
    if (!coords1.points || !coords2.points || coords1.points.length !== coords2.points.length) {
      return false;
    }
    // Check if all points are the same (within small tolerance)
    const tolerance = 1; // 1 pixel tolerance
    for (let i = 0; i < coords1.points.length; i++) {
      const dx = Math.abs(coords1.points[i][0] - coords2.points[i][0]);
      const dy = Math.abs(coords1.points[i][1] - coords2.points[i][1]);
      if (dx > tolerance || dy > tolerance) {
        return false;
      }
    }
    return true;
  }

  async function handlePolygonClick(unit: PropertyUnit, event: React.MouseEvent) {
    const clickedCoords = unit.map_coordinates as { type?: string; points?: number[][] } | null;
    
    // Find all units at the same location (same map_coordinates)
    const unitsAtSameLocation = units.filter((u) => {
      const uCoords = u.map_coordinates as { type?: string; points?: number[][] } | null;
      return arePolygonsSame(clickedCoords, uCoords);
    });

    // If multiple units at same location, show selection popup
    if (unitsAtSameLocation.length > 1) {
      setUnitsAtLocation(unitsAtSameLocation);
      setClickPosition({ x: event.clientX, y: event.clientY });
      return;
    }

    // Single unit - proceed normally
    const contactEmail = typeof window !== 'undefined' 
      ? sessionStorage.getItem('hubspot_contact_email') || undefined
      : undefined;
    
    await trackUnitClick(unit.id, contactEmail);
    handleUnitClick(unit.unit_number);
  }

  async function handleUnitSelection(unit: PropertyUnit) {
    // Close popup
    setUnitsAtLocation(null);
    setClickPosition(null);

    // Track unit click for lead qualification
    const contactEmail = typeof window !== 'undefined' 
      ? sessionStorage.getItem('hubspot_contact_email') || undefined
      : undefined;
    
    await trackUnitClick(unit.id, contactEmail);
    handleUnitClick(unit.unit_number);
  }

  function renderSiteMapPolygon(polygon: SiteMapPolygon) {
    const coords = polygon.map_coordinates as { type?: string; points?: number[][] } | null;
    
    if (!coords || coords.type !== 'Polygon' || !coords.points) {
      return null;
    }

    const unitsAtPolygon = polygonUnitsMap.get(polygon.id) || [];
    const hasUnits = unitsAtPolygon.length > 0;

    // Check if any unit at this polygon is selected or hovered
    const isSelected = hasUnits && unitsAtPolygon.some(u => selectedUnit === u.unit_number);
    const isHovered = hasUnits && unitsAtPolygon.some(u => hoveredUnit === u.unit_number);
    
    // Determine fill color based on availability or unassigned state
    let fillColor = '#e5e7eb'; // default gray for unassigned
    let fillOpacity = 0.2; // Lower opacity for unassigned polygons
    
    if (hasUnits) {
      const firstUnit = unitsAtPolygon[0];
      fillOpacity = 0.3;
      
      if (isSelected) {
        fillColor = '#C87A7A'; // muted red (HSL: 0, 35%, 55%)
        fillOpacity = 0.7;
      } else if (isHovered) {
        fillColor = '#D9A3A3'; // lighter muted red for hover
        fillOpacity = 0.6;
      } else {
        fillColor = getStatusColorHex(firstUnit.availability);
        fillOpacity = 0.6; // Increased from 0.4 for better color visibility
      }
    } else {
      // Unassigned polygon - show with dashed border and lower opacity
      fillColor = '#d1d5db'; // lighter gray
      fillOpacity = 0.15;
    }

    // Convert points array to SVG polygon points string
    const pointsString = coords.points
      .map((point: number[]) => `${point[0]},${point[1]}`)
      .join(' ');

    // Create hover handler that sets hovered unit to first unit
    const handleMouseEnter = () => {
      if (unitsAtPolygon.length > 0) {
        setHoveredUnit(unitsAtPolygon[0].unit_number);
      }
    };

    // Handle polygon click - if multiple units, show popup, otherwise click first unit
    const handlePolygonClickEvent = (e: React.MouseEvent) => {
      if (!hasUnits) {
        // Unassigned polygon - could show a message or do nothing
        console.log(`Polygon ${polygon.block}-${polygon.polygon_number} has no units assigned`);
        return;
      }
      
      if (unitsAtPolygon.length > 1) {
        setUnitsAtLocation(unitsAtPolygon);
        setClickPosition({ x: e.clientX, y: e.clientY });
      } else if (unitsAtPolygon.length === 1) {
        handlePolygonClick(unitsAtPolygon[0], e);
      }
    };

    return (
      <polygon
        key={polygon.id}
        points={pointsString}
        fill={fillColor}
        fillOpacity={fillOpacity}
        stroke={isSelected ? '#A85A5A' : isHovered ? '#C87A7A' : hasUnits ? '#374151' : '#9ca3af'}
        strokeWidth={isSelected ? 3 : isHovered ? 2 : hasUnits ? 1 : 1}
        strokeDasharray={hasUnits ? 'none' : '4 4'}
        strokeOpacity={hasUnits ? 0.8 : 0.5}
        className={hasUnits ? "cursor-pointer transition-all duration-200" : "cursor-default transition-all duration-200"}
        onClick={handlePolygonClickEvent}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHoveredUnit(null)}
        data-polygon-id={polygon.id}
        data-block={polygon.block}
      >
        <title>
          {hasUnits
            ? (unitsAtPolygon.length === 1
                ? `${unitsAtPolygon[0].unit_number} (${formatFloorDisplay(unitsAtPolygon[0].floor)}) - ${unitsAtPolygon[0].category} - ${unitsAtPolygon[0].availability}`
                : `${unitsAtPolygon.length} units - ${polygon.block === 'A' ? 'Canal View' : 'Lagoon View'}-${polygon.polygon_number}`)
            : `Unassigned - ${polygon.block === 'A' ? 'Canal View' : 'Lagoon View'}-${polygon.polygon_number}`}
        </title>
      </polygon>
    );
  }

  const availableCount = units.filter(u => u.availability === 'available').length;
  const reservedCount = units.filter(u => u.availability === 'reserved').length;
  const soldCount = units.filter(u => u.availability === 'sold').length;

  // Calculate active filter count for mobile badge
  const activeFilterCount = [
    activeViewFilter !== 'all',
    activeAvailabilityFilter !== 'all',
    activeCategoryFilter !== 'all',
    activeFloorFilter !== 'all',
  ].filter(Boolean).length;

  // Shared filter button component for consistency
  const FilterButton = ({ 
    active, 
    onClick, 
    children 
  }: { 
    active: boolean; 
    onClick: () => void; 
    children: React.ReactNode 
  }) => (
    <Button 
      variant={active ? 'default' : 'outline'}
      size="sm"
      onClick={onClick}
      className="min-h-[44px] min-w-[44px] lg:min-h-0 lg:min-w-0"
    >
      {children}
    </Button>
  );

  return (
    <div className="space-y-6">
      {/* Filters and Stats */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Mobile Filter Trigger */}
        <Button 
          className="lg:hidden min-h-[44px]" 
          variant="outline"
          onClick={() => setFilterDrawerOpen(true)}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {activeFilterCount > 0 && (
            <Badge className="ml-2 bg-brand-teal text-white">{activeFilterCount}</Badge>
          )}
        </Button>

        {/* Desktop Inline Filters */}
        <div className="hidden lg:flex flex-wrap gap-2">
          {/* View Type Filter */}
          <FilterButton active={activeViewFilter === 'all'} onClick={() => handleViewFilterChange('all')}>
            All Views
          </FilterButton>
          <FilterButton active={activeViewFilter === 'lagoon'} onClick={() => handleViewFilterChange('lagoon')}>
            {getFilterLabel('viewType', 'lagoon')}
          </FilterButton>
          <FilterButton active={activeViewFilter === 'canal'} onClick={() => handleViewFilterChange('canal')}>
            {getFilterLabel('viewType', 'canal')}
          </FilterButton>

          {/* Availability Filter */}
          <FilterButton active={activeAvailabilityFilter === 'all'} onClick={() => handleAvailabilityFilterChange('all')}>
            All Status
          </FilterButton>
          <FilterButton active={activeAvailabilityFilter === 'available'} onClick={() => handleAvailabilityFilterChange('available')}>
            {getFilterLabel('availability', 'available')}
          </FilterButton>
          <FilterButton active={activeAvailabilityFilter === 'reserved'} onClick={() => handleAvailabilityFilterChange('reserved')}>
            {getFilterLabel('availability', 'reserved')}
          </FilterButton>
          <FilterButton active={activeAvailabilityFilter === 'sold'} onClick={() => handleAvailabilityFilterChange('sold')}>
            {getFilterLabel('availability', 'sold')}
          </FilterButton>

          {/* Category Filter */}
          <FilterButton active={activeCategoryFilter === 'all'} onClick={() => handleCategoryFilterChange('all')}>
            All Types
          </FilterButton>
          <FilterButton active={activeCategoryFilter === 'townhouse'} onClick={() => handleCategoryFilterChange('townhouse')}>
            {getFilterLabel('category', 'townhouse')}
          </FilterButton>
          <FilterButton active={activeCategoryFilter === '2bed'} onClick={() => handleCategoryFilterChange('2bed')}>
            {getFilterLabel('category', '2bed')}
          </FilterButton>
          <FilterButton active={activeCategoryFilter === '1bed'} onClick={() => handleCategoryFilterChange('1bed')}>
            {getFilterLabel('category', '1bed')}
          </FilterButton>

          {/* Floor Filter */}
          <FilterButton active={activeFloorFilter === 'all'} onClick={() => handleFloorFilterChange('all')}>
            All Floors
          </FilterButton>
          <FilterButton active={activeFloorFilter === 'GF'} onClick={() => handleFloorFilterChange('GF')}>
            {getFilterLabel('floor', 'GF')}
          </FilterButton>
          <FilterButton active={activeFloorFilter === 'second'} onClick={() => handleFloorFilterChange('second')}>
            {getFilterLabel('floor', 'second')}
          </FilterButton>
          <FilterButton active={activeFloorFilter === 'third'} onClick={() => handleFloorFilterChange('third')}>
            {getFilterLabel('floor', 'third')}
          </FilterButton>
        </div>

        {/* Status Badges */}
        <div className="flex flex-wrap gap-2 lg:gap-4">
          <Badge className="bg-status-available text-white border-status-available">
            Available: {availableCount}
          </Badge>
          <Badge className="bg-status-reserved text-white border-status-reserved">
            Reserved: {reservedCount}
          </Badge>
          {soldCount > 0 && (
            <Badge className="bg-status-sold text-white border-status-sold">
              Sold: {soldCount}
            </Badge>
          )}
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      <Drawer open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader>
            <DrawerTitle>Filter Units</DrawerTitle>
            <DrawerDescription>
              Narrow down units by view, status, type, or floor
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-4 space-y-6 overflow-y-auto">
            {/* View Type */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">View Type</h4>
              <div className="flex flex-wrap gap-2">
                <FilterButton active={activeViewFilter === 'all'} onClick={() => handleViewFilterChange('all')}>
                  All Views
                </FilterButton>
                <FilterButton active={activeViewFilter === 'lagoon'} onClick={() => handleViewFilterChange('lagoon')}>
                  {getFilterLabel('viewType', 'lagoon')}
                </FilterButton>
                <FilterButton active={activeViewFilter === 'canal'} onClick={() => handleViewFilterChange('canal')}>
                  {getFilterLabel('viewType', 'canal')}
                </FilterButton>
              </div>
            </div>

            {/* Availability */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">Availability</h4>
              <div className="flex flex-wrap gap-2">
                <FilterButton active={activeAvailabilityFilter === 'all'} onClick={() => handleAvailabilityFilterChange('all')}>
                  All Status
                </FilterButton>
                <FilterButton active={activeAvailabilityFilter === 'available'} onClick={() => handleAvailabilityFilterChange('available')}>
                  {getFilterLabel('availability', 'available')}
                </FilterButton>
                <FilterButton active={activeAvailabilityFilter === 'reserved'} onClick={() => handleAvailabilityFilterChange('reserved')}>
                  {getFilterLabel('availability', 'reserved')}
                </FilterButton>
                <FilterButton active={activeAvailabilityFilter === 'sold'} onClick={() => handleAvailabilityFilterChange('sold')}>
                  {getFilterLabel('availability', 'sold')}
                </FilterButton>
              </div>
            </div>

            {/* Unit Type */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">Unit Type</h4>
              <div className="flex flex-wrap gap-2">
                <FilterButton active={activeCategoryFilter === 'all'} onClick={() => handleCategoryFilterChange('all')}>
                  All Types
                </FilterButton>
                <FilterButton active={activeCategoryFilter === 'townhouse'} onClick={() => handleCategoryFilterChange('townhouse')}>
                  {getFilterLabel('category', 'townhouse')}
                </FilterButton>
                <FilterButton active={activeCategoryFilter === '2bed'} onClick={() => handleCategoryFilterChange('2bed')}>
                  {getFilterLabel('category', '2bed')}
                </FilterButton>
                <FilterButton active={activeCategoryFilter === '1bed'} onClick={() => handleCategoryFilterChange('1bed')}>
                  {getFilterLabel('category', '1bed')}
                </FilterButton>
              </div>
            </div>

            {/* Floor */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">Floor</h4>
              <div className="flex flex-wrap gap-2">
                <FilterButton active={activeFloorFilter === 'all'} onClick={() => handleFloorFilterChange('all')}>
                  All Floors
                </FilterButton>
                <FilterButton active={activeFloorFilter === 'GF'} onClick={() => handleFloorFilterChange('GF')}>
                  {getFilterLabel('floor', 'GF')}
                </FilterButton>
                <FilterButton active={activeFloorFilter === 'second'} onClick={() => handleFloorFilterChange('second')}>
                  {getFilterLabel('floor', 'second')}
                </FilterButton>
                <FilterButton active={activeFloorFilter === 'third'} onClick={() => handleFloorFilterChange('third')}>
                  {getFilterLabel('floor', 'third')}
                </FilterButton>
              </div>
            </div>
          </div>
          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="outline" className="w-full min-h-[44px]">
                <X className="h-4 w-4 mr-2" />
                Close
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* SVG Map with Zoom/Pan Support */}
      <div className="relative w-full aspect-[16/9] bg-muted rounded-lg border overflow-hidden">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading map...</p>
            </div>
          </div>
        ) : units.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-muted-foreground">No units found with selected filters</p>
          </div>
        ) : (
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={4}
            centerOnInit
            wheel={{ step: 0.1 }}
            pinch={{ step: 5 }}
            doubleClick={{ mode: 'zoomIn', step: 0.5 }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                {/* Zoom Controls */}
                <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8 bg-background/90 backdrop-blur-sm shadow-md"
                    onClick={() => zoomIn()}
                    title="Zoom in"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8 bg-background/90 backdrop-blur-sm shadow-md"
                    onClick={() => zoomOut()}
                    title="Zoom out"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8 bg-background/90 backdrop-blur-sm shadow-md"
                    onClick={() => resetTransform()}
                    title="Reset zoom"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>

                {/* Compass Rose - fixed position, not affected by zoom/pan */}
                <CompassRose
                  size={56}
                  northOffsetDegrees={siteMapConfig?.northOffsetDegrees ?? 0}
                  className="absolute bottom-2 right-2 z-10"
                />
                
                <TransformComponent
                  wrapperClass="!w-full !h-full"
                  contentClass="!w-full !h-full"
                >
                  <svg
                    viewBox={computedViewBox}
                    className="w-full h-full bg-gray-50"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="xMidYMid meet"
                  >
            {/* Render all site map polygons */}
            {siteMapPolygons.map(polygon => renderSiteMapPolygon(polygon))}
            
            {/* Unit labels - render based on site map polygons */}
            {siteMapPolygons.map(polygon => {
              const coords = polygon.map_coordinates as any;
              if (!coords || coords.type !== 'Polygon' || !coords.points || coords.points.length === 0) {
                return null;
              }
              
              const unitsAtPolygon = polygonUnitsMap.get(polygon.id) || [];
              if (unitsAtPolygon.length === 0) {
                return null; // Don't show labels for polygons without units
              }
              
              // Calculate center point for label based on polygon coordinates
              const centerX = coords.points.reduce((sum: number, p: number[]) => sum + p[0], 0) / coords.points.length;
              const centerY = coords.points.reduce((sum: number, p: number[]) => sum + p[1], 0) / coords.points.length;
              
              // If single unit, show unit number; if multiple, show count
              if (unitsAtPolygon.length === 1) {
                return (
                  <text
                    key={`label-${polygon.id}-${unitsAtPolygon[0].id}`}
                    x={centerX}
                    y={centerY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none font-bold"
                    fill="#0f172a"
                    fontSize="18"
                    stroke="white"
                    strokeWidth="2"
                    paintOrder="stroke fill"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3), 0 0 4px rgba(255,255,255,0.9)' }}
                  >
                    {unitsAtPolygon[0].unit_number}
                  </text>
                );
              } else {
                // Multiple units - show polygon identifier or first unit with count
                return (
                  <text
                    key={`label-${polygon.id}`}
                    x={centerX}
                    y={centerY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none font-bold"
                    fill="#0f172a"
                    fontSize="18"
                    stroke="white"
                    strokeWidth="2"
                    paintOrder="stroke fill"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3), 0 0 4px rgba(255,255,255,0.9)' }}
                  >
                    {unitsAtPolygon[0].unit_number}
                    <tspan fontSize="14" fill="#475569" dx="2">
                      ({unitsAtPolygon.length})
                    </tspan>
                  </text>
                );
              }
            })}
            
            {/* Fallback: Render labels for units with individual map_coordinates (not assigned to site map polygons) */}
            {units
              .filter(unit => {
                // Only show units that aren't already covered by site map polygons
                const hasSiteMapPolygon = Array.from(polygonUnitsMap.values())
                  .some(units => units.some(u => u.id === unit.id));
                return !hasSiteMapPolygon;
              })
              .map(unit => {
                const coords = unit.map_coordinates as any;
                if (!coords || coords.type !== 'Polygon' || !coords.points || coords.points.length === 0) {
                  return null;
                }
                
                // Calculate center point for label
                const centerX = coords.points.reduce((sum: number, p: number[]) => sum + p[0], 0) / coords.points.length;
                const centerY = coords.points.reduce((sum: number, p: number[]) => sum + p[1], 0) / coords.points.length;
                
                // Check if there are multiple units at this location
                const unitsAtSameLocation = units.filter((u) => {
                  const uCoords = u.map_coordinates as any;
                  return arePolygonsSame(coords, uCoords);
                });
                
                return (
                  <text
                    key={`label-fallback-${unit.id}`}
                    x={centerX}
                    y={centerY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none font-bold"
                    fill="#0f172a"
                    fontSize="18"
                    stroke="white"
                    strokeWidth="2"
                    paintOrder="stroke fill"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3), 0 0 4px rgba(255,255,255,0.9)' }}
                  >
                    {unit.unit_number}
                    {unitsAtSameLocation.length > 1 && (
                      <tspan fontSize="14" fill="#475569" dx="2">
                        ({unitsAtSameLocation.length})
                      </tspan>
                    )}
                  </text>
                );
              })}
                  </svg>
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        )}
      </div>

      {/* Multi-floor unit selection - Bottom Sheet on mobile, Popup on desktop */}
      {unitsAtLocation && unitsAtLocation.length > 1 && (
        <>
          {/* Mobile: Use Drawer/Bottom Sheet */}
          <Drawer 
            open={unitsAtLocation !== null && typeof window !== 'undefined' && window.innerWidth < 640} 
            onOpenChange={(open) => {
              if (!open) {
                setUnitsAtLocation(null);
                setClickPosition(null);
              }
            }}
          >
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle>Multiple Units at This Location</DrawerTitle>
                <DrawerDescription>Select a unit to view details</DrawerDescription>
              </DrawerHeader>
              <div className="px-4 pb-4 space-y-2 max-h-[50vh] overflow-y-auto">
                {unitsAtLocation.map((unit) => (
                  <button
                    key={unit.id}
                    onClick={() => handleUnitSelection(unit)}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-muted transition-colors border min-h-[56px]"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-base">{unit.unit_number}</span>
                      <Badge variant="outline" className="text-sm">
                        {formatFloorDisplay(unit.floor)}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {unit.category} • {unit.availability}
                    </div>
                  </button>
                ))}
              </div>
              <DrawerFooter>
                <DrawerClose asChild>
                  <Button variant="outline" className="w-full min-h-[44px]">Cancel</Button>
                </DrawerClose>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>

          {/* Desktop: Use positioned popup with viewport awareness */}
          {clickPosition && typeof window !== 'undefined' && window.innerWidth >= 640 && (
            <div
              className="fixed z-50 bg-background border rounded-lg shadow-lg p-4 min-w-[240px] max-w-[320px] hidden sm:block"
              style={{
                left: `${Math.max(16, Math.min(clickPosition.x, window.innerWidth - 336))}px`,
                top: `${Math.max(16, Math.min(clickPosition.y - 10, window.innerHeight - 300))}px`,
                transform: clickPosition.y > window.innerHeight / 2 ? 'translateY(-100%)' : 'translateY(0)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-3">
                <h4 className="text-sm font-semibold mb-1">Multiple Units at This Location</h4>
                <p className="text-xs text-muted-foreground">Select a unit to view:</p>
              </div>
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {unitsAtLocation.map((unit) => (
                  <button
                    key={unit.id}
                    onClick={() => handleUnitSelection(unit)}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors text-sm min-h-[44px]"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{unit.unit_number}</span>
                      <Badge variant="outline" className="text-xs ml-2">
                        {formatFloorDisplay(unit.floor)}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {unit.category} • {unit.availability}
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  setUnitsAtLocation(null);
                  setClickPosition(null);
                }}
                className="mt-3 w-full text-sm text-muted-foreground hover:text-foreground py-2"
              >
                Cancel
              </button>
            </div>
          )}
        </>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-status-available rounded"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-status-reserved rounded"></div>
          <span>Reserved</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-status-sold rounded"></div>
          <span>Sold</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2" style={{ backgroundColor: '#C87A7A', borderColor: '#A85A5A' }}></div>
          <span>Selected</span>
        </div>
      </div>
    </div>
  );
}

